import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import heartIcon from '@/assets/heart.svg';
import heartFilledIcon from '@/assets/heart_filled.svg';
import rightIcon from '@/assets/chevron_right_gray.svg';
import questionIcon from '@/assets/question_grey.svg';
import officialIcon from '@/assets/official_icon.svg';
import Benefits from '@/components/Benefits';
import { formatCurrency } from '@/utils/formatUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';
import useAuthStore from '@/stores/authStore';
import { redirectToLogin } from '@/utils/authUtils';
import { trackEvent } from '@/utils/googleAnalytics';
import OrderRewardBanner from '@/components/OrderRewardBanner';
import { useMetadata } from '@/hooks/useMetadata';

const ListingBuyInfoSection = ({ 
  cardId,
  name, 
  localizedRawName,
  subName,
  desc, 
  notes,
  rawCode,
  rarityInfo,
  set,
  brandId,
  brand, 
  brandImage, 
  price, 
  canList,
  isOfficial,
  condition,
  boxCards = [],
  psaStats = [], 
  conditionStats = [], 
  inventory,
  isSold = false,
  onBuyClick,
  onAttemptList,
  listingId,
  isFavorited = false,
  onFavoriteToggle
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { data: metadata, isLoading: isMetadataLoading } = useMetadata();
  
  // Check if orderRewardTips exists to conditionally render the wrapper div
  const hasOrderRewardTips = !isMetadataLoading && metadata?.orderRewardTips?.icon?.url;

  const displayPrice = formatCurrency(price || 0);

  const handleFavoriteClick = () => {
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    onFavoriteToggle && onFavoriteToggle(!isFavorited);
  };

  const handleListItem = () => {
    trackEvent('listitem_click');
    onAttemptList();
  };

  const handleBuyClick = () => {
    trackEvent('buynow_click');
    onBuyClick();
  };

  const handleBrandClick = () => {
    if (brandId) {
      navigate(`/brands/${brandId}`);
    }
  };

  // Function to get translated condition label using simplified approach
  const getTranslatedCondition = () => {
    if (!condition) return '';
    return t(`sell.product_info.conditions.${condition.toLowerCase()}`);
  };

  return (
    <div className="w-full">
      {/* Title and Subtitle */}
      <div className="mb-4">
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
          <h1 className="text-2xl font-bold text-[#14151a] leading-9">
            {localizedRawName || name}
            {rarityInfo?.sub?.name && `(${rarityInfo.sub.name})`}
          </h1>
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <div className="self-stretch inline-flex justify-start items-center gap-1">
                <span className="text-[#111111] text-sm leading-[21px]">
                  {rarityInfo?.main && (
                    <RarityBadge rarity={rarityInfo.main} className="mr-1" />
                  )}

                  {(set?.code || rawCode) && (
                    <span className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] mr-1 inline-flex justify-center items-center align-middle">
                      <span className="text-[#111111] text-sm font-normal leading-none">
                        {set?.code && rawCode ? `${set.code} ${rawCode}` : set?.code || rawCode}
                      </span>
                    </span>
                  )}

                  <span className="align-middle">{notes}</span>
                </span>
              </div>
            </div>
            <div className="opacity-70 justify-start text-[#aaaaaa] text-sm leading-[21px]">
              {set?.localizedName}
            </div>
          </div>
        </div>
      </div>

      {/* Price and Favorite */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="pl-3 flex items-center gap-1">
            <span className="text-lg font-medium">¥</span>
            <span className="text-[32px] font-medium leading-[48px]">{displayPrice.replace('¥', '')}</span>
          </div>
        </div>
        <button 
          className="px-[35px] py-2.5 rounded-[26px] text-sm flex items-center gap-1"
          onClick={handleFavoriteClick}
        >
          <img 
            src={isFavorited ? heartFilledIcon : heartIcon} 
            alt="Favorite" 
            className="w-[13px] h-3" 
          />
          <span className={`text-sm ${isFavorited ? 'text-[#ff5501]' : 'text-black'}`}>
            {isFavorited ? t('favorite.added') : t('favorite.button')}
          </span>
        </button>
      </div>

      {/* Description */}
      <div className="mb-4">
        <div className="flex items-center gap-[11px] mb-2">
          <div className="text-[#111111] text-sm font-medium">
            {t('listing.product_description')}
          </div>
          <button 
            onClick={() => window.open('/terms/quality_guideline_non_psa', '_blank')}
            className="flex items-center justify-center w-[14px] h-[14px]"
          >
            <img src={questionIcon} alt="Quality guideline" className="w-[14px] h-[14px]" />
          </button>
        </div>
        <div className={`text-base ${desc ? 'text-[#111111]' : 'text-[#666666]'}`}>
          {desc || t('listing.no_description')}
        </div>
      </div>

      {/* Condition */}
      {condition && (
        <div className="mb-4">
          <div className="text-[#111111] text-sm font-medium mb-2">
            {t('listing.product_condition')}
          </div>
          <div className="w-full py-3 rounded-md border border-[#dddddd] flex justify-center items-center">
            <div className="text-center text-[#111111] text-base font-bold">
              {getTranslatedCondition()}
            </div>
          </div>
        </div>
      )}

      {/* Order Reward Banner */}
      {hasOrderRewardTips && (
        <div className="mt-4">
          <OrderRewardBanner orderRewardTips={metadata.orderRewardTips} />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 flex gap-6">
        {canList && (
          <Button 
            variant="default" 
            size="xl" 
            className={isSold ? "w-full" : "w-[167px]"}
            onClick={handleListItem}
          >
            {t('listing.list_item')}
          </Button>
        )}
        {!isSold && (
          <Button 
            variant="primary" 
            size="xl" 
            className={`flex-1 ${isOfficial ? 'bg-[#FF5601] hover:bg-[#e64d00] active:bg-[#cc4400]' : ''}`}
            onClick={handleBuyClick}
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <span className="text-xs">¥</span>
                <span className="text-lg font-medium ml-[2px]">{displayPrice.replace('¥', '')}</span>
              </div>
              {isOfficial && (
                <div className="flex items-center gap-[2px]">
                  <img src={officialIcon} alt="Official" className="block w-[10px] h-[10px]" />
                  <div className="font-bold text-[10px] leading-[10px] translate-y-[0.5px] text-white">
                    {t('box.official')}
                  </div>
                </div>
              )}
            </div>
          </Button>
        )}
      </div>

      {/* Benefits */}
      <Benefits />

      {/* Brand Info */}
      <div
        className="mt-4 h-[72px] bg-white border-t border-b border-[#eeeeee] flex items-center justify-between pr-4 cursor-pointer"
        onClick={handleBrandClick}
      >
        <div className="flex items-center gap-4">
          <img src={brandImage} alt={brand} className="h-[26px] object-contain" />
          <span className="text-sm">{brand}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#aaaaaa]">{t('brand.view_brand')}</span>
          <img src={rightIcon} className="w-5 h-5" alt="View" />
        </div>
      </div>

      {/* Box Cards Info: only render if boxCards has data */}
      {Array.isArray(boxCards) && boxCards.length > 0 && boxCards.flat().length > 0 && (
      <div>
          {/* Title row - move down for vertical centering */}
          <div className="h-[40px] flex items-center bg-white pt-[12px]">
            <span className="text-[14px] font-medium leading-none">{t('listing.related_boxes', 'Related Boxes')}</span>
          </div>
          {/* Card list - height 80px, bottom border */}
          <div className="flex flex-col gap-0 mt-0 pr-4">
            {boxCards.flat().map((item, idx) => (
              <div
                key={item.id || idx}
                className="flex items-center h-[80px] bg-white cursor-pointer border-b border-[#ececec]"
                onClick={() => item.id && navigate(`/card/${item.id}`)}
              >
                {/* Left image section */}
                <div className="w-[56px] h-[56px] rounded bg-white flex items-center justify-center overflow-hidden">
                  <img src={getProductThumbnailUrl(item.images?.[0]?.url)} alt={item.localizedName} className="max-w-[44px] max-h-[44px] object-contain" />
                </div>
                {/* Middle name section */}
                <div className="flex-1 px-4">
                  <span className="text-[12px] text-[#111111] font-normal line-clamp-2 break-all">
                    {item.localizedRawName || item.name}
                    {item.rarityInfo?.sub?.name && `(${item.rarityInfo.sub.name})`}
                  </span>
                </div>
                {/* Right arrow section */}
                <div className="flex items-center justify-center">
                  <img src={rightIcon} alt="chevron right" className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
    </div>
  );
};

export default ListingBuyInfoSection; 
